/**
 * Live status banner shown while a cache convergence run is in flight for
 * this flow. Convergence forks the flow, iterates the fork's cache with
 * cache-only runs, and — on success — promotes the proven cache back onto
 * this (origin) flow (see `useCacheConvergence.ts` and
 * `apps/api/src/workflows/routes/workflow-convergence.routes.ts`).
 *
 * The warning line calls out that edits made while a run is active can be
 * clobbered: promotion overwrites the origin flow's cache once the fork
 * converges, so any playbook/cache edits made here in the meantime are not
 * guaranteed to survive.
 *
 * @module ui/pages/FlowDesigner/OptimizingBanner
 */
import { Sparkle } from '@phosphor-icons/react';
import { Banner, Text, Flex } from '@chatsdet/ui';
import type { ActiveOptimization, CacheConvergencePhase } from './hooks/useCacheConvergence';
import { formatConvergenceProofAttempts } from './runs/convergence-run-formatters';

const PHASE_LABELS: Record<CacheConvergencePhase, string> = {
  preflight: 'Preparing',
  fork: 'Forking',
  converge: 'Caching',
  verify: 'Verifying',
  promote: 'Promoting',
  origin_gate: 'Final check',
  finalize: 'Finishing',
};

type OptimizingBannerProps = {
  optimization: NonNullable<ActiveOptimization>;
};

export function OptimizingBanner({ optimization }: OptimizingBannerProps) {
  const { status, phase, cycleCount, cycleCap } = optimization;
  // 'queued' means the job hasn't been picked up yet, so its `phase` is still
  // the row's initial value and would misreport progress — show "Starting"
  // until the job actually advances the phase itself.
  const phaseLabel =
    status === 'queued' || phase === null ? 'Starting' : (PHASE_LABELS[phase] ?? 'Starting');

  return (
    // `bottom-16` rather than HistoryBanner's `bottom-4`: the canvas's
    // "N steps below" pill is pinned `bottom-3 z-20` at the same horizontal
    // center, so a bottom-4 banner renders UNDER it. Sit above the pill
    // instead of fighting it for the same 16px.
    <div className="absolute bottom-16 left-1/2 -translate-x-1/2 z-10">
      <Banner variant="info" className="shadow-lg whitespace-nowrap">
        <Flex direction="column" gap="xs">
          <Flex align="center" gap="sm">
            <Sparkle size={18} className="text-blue-600 dark:text-blue-400 shrink-0" />
            <Text size="sm" weight="medium">
              Optimizing this flow &mdash; {phaseLabel}
              {cycleCount > 0 && (
                <span className="ml-1">
                  &bull; {formatConvergenceProofAttempts(cycleCount, cycleCap)}
                </span>
              )}
            </Text>
          </Flex>
          <Text size="xs" className="text-blue-600 dark:text-blue-400">
            Edits made while optimization runs may be overwritten when the proven cache is promoted.
          </Text>
        </Flex>
      </Banner>
    </div>
  );
}
